import {
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  inject,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LayoutComponent } from '../../core/layout/layout.component';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';
import { MatOptionModule } from '@angular/material/core';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { DepartmentsService } from '../../features/departments/services/departments.service';
import { TasksService } from '../../features/tasks/services/tasks.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ServiceManagementService } from '../../features/service-management/services/service-management.service';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { EmployeeService } from '../../features/employees/employee.service';
import { PermissionService } from '../../shared/services/permission.service';
import { ModuleIds } from '../../shared/permission-constants/permission-const';
import { ProjectService } from '../../features/projects/services/projects.service';
import { TemplateService } from '../../features/notification-template/services/notification-template.service';

@Component({
  selector: 'app-branch',
  standalone: true,
  imports: [
    LayoutComponent,
    AgGridAngular,
    MatButtonModule,
    MatDialogModule,
    CommonModule,
  ],
  templateUrl: './template.component.html',
  styleUrls: ['./template.component.scss'],
})
export class TemplateComponent {
  public viewPortSize: boolean = false;
  public rowData: any[] = [];

  canAddNotification$ = this.permissionsService.hasPermission(
    ModuleIds.NotificationTemplate,
    'Create'
  );
  canEditNotification$ = this.permissionsService.hasPermission(
    ModuleIds.NotificationTemplate,
    'Edit'
  );
  canDeleteNotification$ = this.permissionsService.hasPermission(
    ModuleIds.NotificationTemplate,
    'Delete'
  );
  canEditNotification: boolean = false;
  canDeleteNotification: boolean = false;

  public columnDefs: ColDef[] = [
    {
      field: 'taskName',
      headerName: 'Task Name',
      flex: this.viewPortSize ? 1 : 0,
    },
    {
      field: 'taskCode',
      headerName: 'Task Code',
      flex: this.viewPortSize ? 1 : 0,
    },
    {
      field: 'price',
      headerName: 'Price',
      flex: this.viewPortSize ? 1 : 0,
    },
    {
      field: 'estimatedTime',
      headerName: 'Estimated Time',
      flex: this.viewPortSize ? 1 : 0,
    },
    {
      field: 'action',
      headerName: 'Action',
      // flex: this.viewPortSize ? 1 : 0,
      cellRenderer: (params: any) => {
        const div = document.createElement('div');
        div.style.display = 'flex';
        div.style.alignItems = 'center';
        div.style.gap = '20px';

        const createButton = (iconSrc: string, onClick: () => void) => {
          const span = document.createElement('span');
          span.style.cursor = 'pointer';
          span.style.display = 'flex';
          span.innerHTML = `<img src="${iconSrc}"/>`;
          span.addEventListener('click', onClick);
          return span;
        };
        div.appendChild(
          createButton('assets/images/icons/icon-edit.svg', () =>
            this.openEditDialog(params?.data?.id)
          )
        );
        div.appendChild(
          createButton('assets/images/icons/icon-delete.svg', () =>
            this.onDeleteTask(params.data?.id)
          )
        );
        return div;
      },
      floatingFilter: false,
      filter: false,
    },
  ];
  public defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  };
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public paginationPageSize = 10;
  public paginationPageSizeSelector: number[] | boolean = [10];
  public themeClass: string = 'ag-theme-quartz';
  public loading: boolean = true;
  public error: any = null;

  private searchSubject = new Subject<string>();
  profileName: any;
  taskData: any;

  constructor(
    private serviceManagementService: ServiceManagementService,
    private toastr: ToastrService,
    private route: Router,
    private permissionsService: PermissionService,
    private templateService: TemplateService
  ) {
    const loggedInUser: any = localStorage.getItem('loggedInUser');
    const userData = JSON.parse(loggedInUser);
    this.profileName = userData?.profileData?.profileName;
  }

  ngOnInit() {
    this.notificationTemplates();
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.notificationTemplates(searchTerm);
      });

    this.canEditNotification$.subscribe((canEdit) => {
      this.canEditNotification = canEdit;
    });
    this.canDeleteNotification$.subscribe((canDelete) => {
      this.canDeleteNotification = canDelete;
    });

    this.checkViewportSize();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkViewportSize();
  }

  checkViewportSize() {
    if (window.innerWidth > 1200) {
      this.viewPortSize = true;
    } else {
      this.viewPortSize = false;
    }
    this.setAGColumn();
  }

  setAGColumn() {
    this.columnDefs = [
      {
        field: 'templateName',
        headerName: 'Template Name',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'code',
        headerName: 'Template Code',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'channel',
        headerName: 'Channel',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'action',
        headerName: 'Action',
        // flex: this.viewPortSize ? 1 : 0,
        cellRenderer: (params: any) => {
          const div = document.createElement('div');
          div.style.display = 'flex';
          div.style.alignItems = 'center';
          div.style.gap = '20px';

          const createButton = (iconSrc: string, onClick: () => void) => {
            const span = document.createElement('span');
            span.style.cursor = 'pointer';
            span.style.display = 'flex';
            span.innerHTML = `<img src="${iconSrc}"/>`;
            span.addEventListener('click', onClick);
            return span;
          };

          if(this.canEditNotification) {
          div.appendChild(
            createButton('assets/images/icons/icon-edit.svg', () =>
              this.openEditDialog(params?.data?.id)
            )
          );
          }

          if(this.canDeleteNotification){
          div.appendChild(
            createButton('assets/images/icons/icon-delete.svg', () =>
              this.onDeleteTask(params.data?.id)
            )
          );
          }
          return div;
        },
        floatingFilter: false,
        filter: false,
      },
    ];
  }

  onDeleteTask(templateId: number): void {
    const dialogRef = this.dialog.open(ConfirmDialog);

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.deleteTemplate(templateId);
      }
    });
  }

  private deleteTemplate(templateId: number): void {
    this.templateService.removeNotificationTemplate(templateId).subscribe({
      next: (resp) => {
        this.toastr.success('Template deleted successfully');
        this.notificationTemplates();
      },
      error: (error: any) => {
        this.toastr.error(error, 'Error');
        this.loading = false;
      },
    });
  }

  private notificationTemplates(search: string = '') {
    this.loading = true;
    this.templateService.notificationTemplates(search).subscribe({
      next: (templates) => {
        this.rowData = templates.map((template: any) => {
          return {
            id: template?.id || '--',
            templateName: template?.templateName || '--',
            code: template?.code || '--',
            channel: template?.channel || '--',
          };
        });

        this.loading = false;
      },
      error: (error) => {
        this.error = error;
        this.loading = false;
      },
    });
  }


  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  readonly dialog = inject(MatDialog);

  openConfirm() {
    const dialogRef = this.dialog.open(ConfirmDialog);
  }

  openDialog() {
    this.route.navigate(['notification-template/add-template']);
  }

  openEditDialog(templateId: any) {
    this.route.navigate(['notification-template/edit-template', templateId]);
  }
}

const gridDiv = document.querySelector<HTMLElement>('#myGrid')!;

@Component({
  selector: 'add-branch-dialog',
  templateUrl: './add-template.html',
  styleUrls: ['./template.component.scss'],
  standalone: true,
  imports: [
    // MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatOptionModule,
    CommonModule,
    FormsModule,
    DragDropModule,
  ],
})
export class AddTemplateComponent implements OnInit {
  @Output() branchAdded = new EventEmitter<any>();
  templateForm!: FormGroup;
  itemArray: any[] = [];
  taskData: any;
  templateData: any;
  loading: boolean = false;
  editIndex: number | null = null;
  templateId: string | null;
  readonly dialog = inject(MatDialog);
  departmentData: any;
  employees: any;
  parsedBody = '';
  placeholderArray: any[] = [];

  constructor(
    private serviceManagementService: ServiceManagementService,
    private toastr: ToastrService,
    private http: HttpClient,
    private fb: FormBuilder,
    private route: Router,
    private router: ActivatedRoute,
    private taskService: TasksService,
    private employeeService: EmployeeService,
    private templateService: TemplateService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.router.paramMap.subscribe((params) => {
      this.templateId = params.get('id');
      this.notificationTemplate(Number(this.templateId));
    });
  }

  initForm(): void {
    this.templateForm = this.fb.group({
      templateName: [
        this.templateData?.templateName || '',
        Validators.required,
      ],
      code: [this.templateData?.code || '', Validators.required],
      channel: [this.templateData?.channel || '', Validators.required],
      subject: [this.templateData?.subject || '', Validators.required],
      body: [this.templateData?.body || '', Validators.required],
      placeholders: [''],
    });

     // parse the incoming body
  // const rawBody = this.templateData?.body || '';
  // this.parsedBody = this.parseTemplate(rawBody);
  }

  notificationTemplate(id: any) {
    if (id) {
      this.templateService.notificationTemplate(id).subscribe({
        next: (template) => {
          this.templateData = template;
          template?.placeholders.forEach((placeholder: any) => {
            this.placeholderArray.push(placeholder);
          })
          this.initForm();
        },
        error: (error) => {
          this.toastr.error(error, 'Error');
        },
      });
    }
  }

  onRemove(index: number) {
    this.placeholderArray.splice(index, 1);
  }

  // parseTemplate(text: string): string {
  //   // Wrap each placeholder with readonly span
  //   return text.replace(/\{\{(.*?)\}\}/g,
  //     (_, token) => `<span class="readonly" contenteditable="false">{{${token}}}</span>`
  //   );
  // }
  
  // onInput(event: Event): void {
  //   const div = event.target as HTMLElement;
  
  //   // Restore spans back into {{token}}
  //   const restoredValue = div.innerHTML.replace(
  //     /<span class="readonly" contenteditable="false">\{\{(.*?)\}\}<\/span>/g,
  //     '{{$1}}'
  //   );
  
  //   this.templateForm.get('body')?.setValue(restoredValue);
  // }

  addPlaceholder(placeholder: string) {
    if (placeholder.trim()) {
      this.placeholderArray.push(placeholder);
      console.log('placeholderArray', this.placeholderArray);
      this.templateForm.get('placeholders')?.setValue('');
    }
  }

  selectPlaceholder(placeholder: string, textarea: HTMLTextAreaElement) {
    const ctrl = this.templateForm.get('body');
    if (!ctrl) return;
  
    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const currentValue = ctrl.value || '';
  
    // Insert at cursor position
    const newValue =
      currentValue.substring(0, start) +
      '{{' + placeholder + '}}' +
      currentValue.substring(end);
  
    ctrl.setValue(newValue);
  
    // Put cursor after inserted placeholder
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + placeholder.length + 4; // 4 for `{{}}`
      textarea.focus();
    });
  }
  
  
  
  

  // onEditItem(id: any) {
  //   const item = this.itemArray[id];
  //   this.templateForm.get('taskName')?.setValue(item?.taskName);
  //   this.editIndex = id;
  // }

  // onRemoveItem(id: number) {
  //   if (this.templateData?.id && this.itemArray[id]?.id) {
  //     const dialogRef = this.dialog.open(ConfirmDialog);

  //     dialogRef.afterClosed().subscribe((result: any) => {
  //       if (result === true) {
  //         this.serviceManagementService
  //           .removeServiceTask(this.itemArray[id]?.id)
  //           .subscribe({
  //             next: () => {
  //               this.toastr.success('Item removed successfully!', 'Success');
  //               this.itemArray = this.itemArray
  //                 .filter((_, index) => index !== id)
  //                 .map(({ __typename, ...rest }) => rest);
  //             },
  //             error: (error) => {
  //               this.toastr.error(error, 'Error');
  //               this.loading = false;
  //             },
  //           });
  //       }
  //     });
  //   } else {
  //     // New item, remove safely
  //     this.itemArray = this.itemArray.filter((_, index) => index !== id);
  //   }

  //   // Reset form if the removed item was being edited
  //   if (this.editIndex === id) {
  //     this.templateForm.get('taskName')?.reset();
  //     this.editIndex = null;
  //   }
  // }

  // trackByIndex(index: number, item: any): number {
  //   return index;
  // }

  // dropItem(event: CdkDragDrop<any[]>) {
  //   console.log('this.itemArray',this.itemArray);
  //   console.log('event',event);

  //   moveItemInArray(this.itemArray, event.previousIndex, event.currentIndex);
  // }

  onAddTemplate(): void {
    if (this.templateForm.invalid) {
      this.templateForm.markAllAsTouched();
      return;
    }

    if (!this.templateData) {
      this.loading = true;
      const createServiceInput = {
        templateName: this.templateForm.value.templateName,
        code: this.templateForm.value.code,
        channel: this.templateForm.value.channel,
        subject: this.templateForm.value.subject,
        body: this.templateForm.value.body,
        placeholders: this.placeholderArray
      };
      this.templateService
        .createNotificationTemplate(createServiceInput)
        .subscribe(
          (response) => {
            this.loading = false;
            this.toastr.success('Template added successfully!', 'Success');
            this.route.navigate(['/notification-template']);
          },
          (error) => {
            this.loading = false;
            let errorMessage = 'Failed to add template. Please try again.';

            this.toastr.error(errorMessage, 'Error');
          }
        );
    } else {
      if (this.templateForm.valid) {
        this.loading = true;
        const updateTaskInput = {
          id: Number(this.templateData?.id),
          templateName: this.templateForm.value.templateName,
          code: this.templateForm.value.code,
          channel: this.templateForm.value.channel,
          subject: this.templateForm.value.subject,
          body: this.templateForm.value.body,
          placeholders: this.placeholderArray
        };

        this.templateService
          .updateNotificationTemplate(
            Number(this.templateData?.id),
            updateTaskInput
          )
          .subscribe(
            (response) => {
              this.loading = false;
              this.toastr.success('Template updated successfully!', 'Success');
              this.route.navigate(['/notification-template']);
            },
            (error) => {
              this.loading = false;
              let errorMessage = 'Failed to update template. Please try again.';
              this.toastr.error(errorMessage, 'Error');
            }
          );
      }
    }
  }
}
